// Docket: tasks in lists, with an optional day to do them and an optional
// deadline. A repeating task is one task whose dates move on each time it is
// finished; each finish is recorded in the Logbook. Kept free of the stores so
// the rules can be tested on their own.

export const DOCKET_COLORS = ['accent', 'blue', 'success', 'warning', 'error', 'muted'] as const
type DocketColor = typeof DOCKET_COLORS[number]

interface DocketStep {
  id: string
  title: string
  done: boolean
}

export type DocketRepeat =
  /** Every N days from the task's date. */
  | { kind: 'daily'; every: number }
  /** On these weekdays (0 = Sunday). */
  | { kind: 'weekly'; days: number[] }
  /** On this day of the month; the month's last day when it is shorter. */
  | { kind: 'monthly'; day: number }
  /** N days after it was last finished. */
  | { kind: 'after'; days: number }
  /**
   * A calendar event's prep task: due N days before each occurrence. The
   * event decides its dates (see utils/prepTasks); `occurrence` is the one
   * it is for, as the series planned it.
   */
  | { kind: 'event'; eventId: string; leadDays: number; occurrence: string }

/**
 * A reminder on the phone, before the task's day or its deadline. `before` is
 * in minutes, counted back from the date's time (or the writer's default
 * time, when the date has none). A repeating task is reminded of each time.
 */
export interface TaskReminder {
  on: 'do' | 'due'
  before: number
}

/** Lead times a reminder can have, in minutes. */
export const REMIND_BEFORE = [0, 5, 15, 30, 60, 120, 1440, 2880, 10080] as const

export interface DocketTask {
  id: string
  title: string
  /** Plain text. Called "details" because "notes" are documents in this app. */
  details: string
  /** null is Docket (no list). */
  listId: string | null
  steps: DocketStep[]
  /** The day it is planned for, 'YYYY-MM-DD'; puts it in Today from then on. */
  doOn: string | null
  /** A time on that day, 'HH:MM'; null for any time that day. */
  doAt: string | null
  /** A deadline, 'YYYY-MM-DD'. */
  dueOn: string | null
  /** The deadline's time, 'HH:MM'; null for the end of that day. */
  dueAt: string | null
  /**
   * A reminder on the phone. null follows the writer's setting (no reminder,
   * unless they asked to be reminded of every dated task); 'off' never.
   */
  remind: TaskReminder | 'off' | null
  repeat: DocketRepeat | null
  /** The page, folder, or desk the task is about. */
  linkedId: string | null
  done: boolean
  /** Milliseconds; when it was finished. */
  doneAt: number | null
  /** Position within its list; fractional so a move rewrites one row. */
  order: number
  createdAt: number
}

export interface DocketList {
  id: string
  name: string
  color: DocketColor
  icon: string | null
  /** The desk the list belongs to; its tasks count as inside that desk. */
  deskId: string | null
  /** Ticked items stay crossed out until the list is reset. */
  checklist: boolean
  order: number
  archived: boolean
}

/** A finished task, as kept in the Logbook. */
export interface LogbookEntry {
  taskId: string
  title: string
  listId: string | null
  linkedId: string | null
  /** The day it was finished, 'YYYY-MM-DD'. */
  day: string
  doneAt: number
}

/** The fixed ids of lists made for everyone, so devices that make them at once agree. */
export const WRITING_LIST_ID = 'writing'
export const MILESTONES_LIST_ID = 'milestones'

const DAY = /^\d{4}-\d{2}-\d{2}$/
const day = (value: unknown): string | null => (typeof value === 'string' && DAY.test(value) ? value : null)
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/
export const timeOfDay = (value: unknown): string | null => (typeof value === 'string' && TIME.test(value) ? value : null)
const text = (value: unknown, max: number) => (typeof value === 'string' ? value.slice(0, max) : '')
const finite = (value: unknown, fallback: number) => (typeof value === 'number' && Number.isFinite(value) ? value : fallback)

function whole(value: unknown, min: number, max: number, fallback: number): number {
  const n = Math.round(Number(value))
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback
}

export function parseRepeat(raw: any): DocketRepeat | null {
  switch (raw?.kind) {
    case 'daily': return { kind: 'daily', every: whole(raw.every, 1, 365, 1) }
    case 'weekly': {
      const days = Array.isArray(raw.days)
        ? [...new Set<number>(raw.days.filter((d: unknown) => Number.isInteger(d) && (d as number) >= 0 && (d as number) <= 6))].sort()
        : []
      return days.length ? { kind: 'weekly', days } : null
    }
    case 'monthly': return { kind: 'monthly', day: whole(raw.day, 1, 31, 1) }
    case 'after': return { kind: 'after', days: whole(raw.days, 1, 365, 1) }
    case 'event':
      return typeof raw.eventId === 'string' && raw.eventId && day(raw.occurrence)
        ? { kind: 'event', eventId: raw.eventId, leadDays: whole(raw.leadDays, 0, 60, 1), occurrence: raw.occurrence }
        : null
    default: return null
  }
}

function parseReminder(raw: any): TaskReminder | 'off' | null {
  if (raw === 'off') return 'off'
  if (!raw || typeof raw !== 'object') return null
  return {
    on: raw.on === 'due' ? 'due' : 'do',
    before: (REMIND_BEFORE as readonly number[]).includes(raw.before) ? raw.before : 0,
  }
}

export function parseTask(raw: any): DocketTask | null {
  if (!raw || typeof raw.id !== 'string' || !raw.id || typeof raw.title !== 'string') return null
  const steps: DocketStep[] = Array.isArray(raw.steps)
    ? raw.steps
      .filter((s: any) => s && typeof s.id === 'string' && typeof s.title === 'string')
      .slice(0, 200)
      .map((s: any) => ({ id: s.id, title: s.title.slice(0, 500), done: s.done === true }))
    : []
  return {
    id: raw.id,
    title: raw.title.slice(0, 500),
    details: text(raw.details, 20_000),
    listId: typeof raw.listId === 'string' && raw.listId ? raw.listId : null,
    steps,
    doOn: day(raw.doOn),
    // A time belongs to its date and goes with it.
    doAt: day(raw.doOn) ? timeOfDay(raw.doAt) : null,
    dueOn: day(raw.dueOn),
    dueAt: day(raw.dueOn) ? timeOfDay(raw.dueAt) : null,
    remind: parseReminder(raw.remind),
    repeat: parseRepeat(raw.repeat),
    linkedId: typeof raw.linkedId === 'string' && raw.linkedId ? raw.linkedId : null,
    done: raw.done === true,
    doneAt: raw.done === true ? finite(raw.doneAt, 0) : null,
    order: finite(raw.order, 0),
    createdAt: finite(raw.createdAt, 0),
  }
}

export function parseList(raw: any): DocketList | null {
  if (!raw || typeof raw.id !== 'string' || !raw.id || typeof raw.name !== 'string') return null
  return {
    id: raw.id,
    name: raw.name.slice(0, 120),
    color: DOCKET_COLORS.includes(raw.color) ? raw.color : 'accent',
    icon: typeof raw.icon === 'string' && raw.icon ? raw.icon : null,
    deskId: typeof raw.deskId === 'string' && raw.deskId ? raw.deskId : null,
    checklist: raw.checklist === true,
    order: finite(raw.order, 0),
    archived: raw.archived === true,
  }
}

export function parseLogbookEntry(raw: any): LogbookEntry | null {
  if (!raw || typeof raw.taskId !== 'string' || typeof raw.title !== 'string' || !day(raw.day)) return null
  return {
    taskId: raw.taskId,
    title: raw.title.slice(0, 500),
    listId: typeof raw.listId === 'string' && raw.listId ? raw.listId : null,
    linkedId: typeof raw.linkedId === 'string' && raw.linkedId ? raw.linkedId : null,
    day: raw.day,
    doneAt: finite(raw.doneAt, 0),
  }
}

export function blankTask(id: string, now: number, patch: Partial<DocketTask> = {}): DocketTask {
  return {
    id,
    title: '',
    details: '',
    listId: null,
    steps: [],
    doOn: null,
    doAt: null,
    dueOn: null,
    dueAt: null,
    remind: null,
    repeat: null,
    linkedId: null,
    done: false,
    doneAt: null,
    order: 0,
    createdAt: now,
    ...patch,
  }
}

/** A milestone from the Goals tool, before Docket; it keeps its id. */
export function taskFromMilestone(raw: any, order: number): DocketTask | null {
  if (!raw || typeof raw.id !== 'string' || typeof raw.title !== 'string' || !raw.title.trim()) return null
  const createdAt = finite(raw.createdAt, 0)
  return blankTask(raw.id, createdAt, {
    title: raw.title.trim(),
    listId: MILESTONES_LIST_ID,
    dueOn: day(raw.due),
    done: raw.done === true,
    doneAt: raw.done === true ? createdAt : null,
    order,
  })
}
